import { useLocation } from '@solidjs/router'
import { useLocaleNavigate } from '../../../utils/locale-navigation.js'
import {
    createContext,
    createEffect,
    createSignal,
    mergeProps,
    onCleanup,
    onMount,
    splitProps,
} from 'solid-js'
import { createStore } from 'solid-js/store'
import countStore from '../../../../stores/count-store.js'
import errorStore from '../../../../stores/error-store.js'
import { captureError } from '../../../../utilities/use-sentry.js'
import sessionStore from '../../../../stores/session-store.js'
import userStore from '../../../../stores/user-store.js'
import bookingStore from '../../../../stores/booking-store.js'
import {
    trackConversionEvent,
    trackEvent,
} from '../../../../utilities/use-analytics'
import { capture as posthogCapture } from '../../../../utilities/use-posthog.js'
import { currentCurrency } from '../../../utils/analytics-guest-flow.js'
import SessionService from '../../../services/session-service.js'
import { useAuthModal } from '../../../contexts/auth-modal-context.jsx'
import inboxStore from '../../../../stores/inbox-store.js'
import { platform } from '../../../../../.configs/app/environment.js'

/**
 * Where the header logo goes. On the web that is the marketing homepage; the
 * mobile app does not include that page, so it returns to the app's landing
 * surface instead. Module scope, not inside the controller — it depends on
 * build configuration only, and the controller is already at its complexity
 * ceiling.
 */
const logoHref = platform.isMobileApp ? '/search' : '/'

// TENANT-5036: `BookingService`/`PropertyService`/`ProfileService`/`InboxService`
// are only ever called below behind an `isAuthenticated()` (or
// `isAuthenticated() && isDashboardPage()`) guard, so anonymous visitors -
// which is exactly what marketing pages like `/` and `/become-a-host` get in
// practice, and what Lighthouse measures - never actually need them. This
// template controller wraps every page in the app, so statically importing
// them here shipped ~45KB of authenticated-only service/API-client code to
// every anonymous visitor's boot chunk. Dynamic-importing them only when an
// authenticated code path actually runs keeps behavior identical for signed-in
// users (one extra async hop, well after first paint) while keeping them out
// of the eagerly-loaded main bundle entirely.
const loadPropertyService = () => import('../../../services/property-service').then(m => m.default)
const loadBookingService = () => import('../../../services/booking-service').then(m => m.default)
const loadProfileService = () => import('../../../services/profile-service').then(m => m.default)
const loadInboxService = () => import('../../../services/inbox-service').then(m => m.default)
import { useIntl } from '@cookbook/solid-intl'
import { messages } from './public-template-component.messages.en.js'
export const PublicTemplateContext = createContext({
    isContextReady: () => false,
})

export const usePublicTemplateComponentController = (props) => {
    const intl = useIntl()
    const [
        requestAttributes,
        styles,
        attributes,
    ] = splitProps(
        mergeProps({
            overflow: false,
            showFooter: false,
            showHeader: false,
            showHeaderMenu: false,
            showSidebar: false,
            showUserHeader: false,
        }, props),
        ['logo'],
        ['class'],
    )

    const navigate = useLocaleNavigate()

    const location = useLocation()

    const [
        showHamburgerMenu,
        setShowHamburgerMenu,
    ] = createSignal()
    const [
        showAuthModal,
        setShowAuthModal,
    ] = createSignal(false)
    const [
        isAuthenticated,
        setIsAuthenticated,
    ] = createSignal()

    const toggleHamburgerMenu = () => {
        setShowHamburgerMenu(previous => !previous)
    }

    const hideHamburgerMenuBreakpoint = 768

    // Hide hamburger menu when screen resizes past breakpoint.
    //
    // `matchMedia` is created inside `onMount`, not at controller scope: this template wraps the
    // location landing pages too, and those are prerendered, where `window` does not exist. Using
    // `matchMedia` rather than `window.innerWidth` still matters — it aligns with the CSS media
    // queries in a way a width read does not.
    onMount(() => {
        const hamburgerMenuMediaQuery = window.matchMedia(`(min-width: ${hideHamburgerMenuBreakpoint}px)`)
        const handleResize = (event) => {
            if (event.matches && showHamburgerMenu()) {
                setShowHamburgerMenu(false)
            }
        }

        hamburgerMenuMediaQuery.addEventListener('change', handleResize)
        // `onMount` ignores a returned function — the listener this replaces was never removed.
        onCleanup(() => hamburgerMenuMediaQuery.removeEventListener('change', handleResize))
    })

    const [
        state,
        setState,
    ] = createStore({
        authRedirectUrl: null,
        isContextReady: false,
        lockCurrencySelection: false,
        showFooter: false,
        showFullWidthHeader: false,
        showHeader: false,
        showHeaderMenu: false,
        showLocaleSelectModal: false,
        // TENANT-5433: gates the ≤767px hamburger button independently of
        // `showHeaderMenu` (the desktop nav), so a stripped-chrome route can
        // drop the mobile menu without changing the routes that keep it.
        showMobileMenu: false,
        showPartnerHeader: false,
        showReferralRecipientBanner: false,
        showReferralRecipientModal: false,
        showReferralSenderBanner: false,
        showReferralSenderModal: false,
        showSidebar: false,
        showUserHeader: false,
        // Which property-setup wizard the current route belongs to
        // (ADD_PROPERTY_SECTION.SETUP_NATIVE | SETUP_EXTERNAL), or undefined off
        // the wizard. Drives the header's "Need Help?" button.
        // TENANT-5634: whether the wizard on screen is the QR-shortened one. The
        // header's help button lives in the template, outside the wizard's own
        // context, so the wizard page reports it here and the help content drops
        // the questions about sections a QR host never sees (the damage deposit).
        wizardHelpQrHost: false,
        wizardHelpSection: undefined,
        // TENANT-5616: on the connect pages the current screen is component state
        // on one URL, so the page reports it here (`CONNECT_STEP`) and the help
        // button reads it instead of the URL. Undefined off those pages and on
        // their terminal "connected" screen, which carries no help.
        wizardHelpStep: undefined,
    })

    const value = {
        clearAuthRedirectUrl: () => setState('authRedirectUrl', null),
        isContextReady: () => true,
        setAuthRedirectUrl: (url) => setState('authRedirectUrl', url),
        setLockCurrencySelection: (value) => setState('lockCurrencySelection', value),
        setShowFooter: (value) => setState('showFooter', value),
        setShowFullWidthHeader: (value) => setState('showFullWidthHeader', value),
        setShowHeader: (value) => setState('showHeader', value),
        setShowHeaderMenu: (value) => setState('showHeaderMenu', value),
        setShowMobileMenu: (value) => setState('showMobileMenu', value),
        setShowPartnerHeader: (value) => setState('showPartnerHeader', value),
        setShowSidebar: (value) => setState('showSidebar', value),
        setShowUserHeader: (value) => setState('showUserHeader', value),
        setWizardHelpQrHost: (value) => setState('wizardHelpQrHost', Boolean(value)),
        setWizardHelpSection: (value) => setState('wizardHelpSection', value),
        setWizardHelpStep: (value) => setState('wizardHelpStep', value),
        state,
        updateAuthenticatedData: () => updateAuthenticatedData(),
    }

    const logout = async () => {
        try {
            await SessionService.delete({
                intentional: true,
            })
            userStore.clear()
            // The app has no homepage, so logging out there would land the user on a
            // surface it does not include. `logoHref` is the same value the header
            // logo uses: '/' on the web, '/search' in the app.
            navigate(logoHref)
            setState('showSidebar', false)
            setState('showHeaderMenu', true)
            trackEvent('clickAccordianCTA_Logout')
            trackEvent('Triggered_homepage')
        } catch (error) {
            console.error(error)
            errorStore.addError(error, 'Logout in sidebarNav')
        } finally {
            closeHamburgerMenu()
        }
    }

    const properties = []
    const stays = []

    // TODO: Replace this hardcoded list with a per-route flag.
    //
    // Currently this list is the only "is this a dashboard page?" source of truth in the
    // app, and it's only consulted by `checkAndSetBannerVisibility()` below to gate the
    // referral banners. The login-redirect logic does NOT use it — it uses the presence
    // of the `from` query param (set by `useAuthGuard`) as an equivalent signal, which
    // is more accurate.
    //
    // Issues with the current shape:
    //   - String-prefix matching (e.g. `/listing` matches `/listing-public/abc` even if
    //     that page were ever non-dashboard) is fragile.
    //   - The list omits guarded routes that arguably belong here (e.g. `/inbox`),
    //     so anything that grows to rely on this helper will silently miss them.
    //   - New dashboard routes don't self-describe — contributors have to remember to
    //     update this list in a sibling component.
    //
    // Recommended fix when revisited: add an `isDashboard: true` flag to each route file
    // in `src/app/routes/web/*.js`, expose it via the route loader, and have this helper
    // (or its replacement) read from the matched route's metadata. That kills the string
    // matching and makes new dashboard routes self-declaring.
    const isDashboardPage = () => {
        const path = location.pathname
        const dashboardPages = [
            '/properties', '/property', '/listing', '/account', '/stays', '/bookings', '/search', '/points', '/referral-center',
        ]
        return dashboardPages.some(page => path.startsWith(page))
    }

    // Check profile data and set banner visibility
    const checkAndSetBannerVisibility = async () => {

        setState('showReferralRecipientBanner', false)
        setState('showReferralSenderBanner', false)

        try {
            // Only show banner on dashboard pages, not welcome
            if (!isDashboardPage()) {
                return
            }

            // Fetch user profile to check show_referral_recipient_banner
            const ProfileService = await loadProfileService()
            const profile = await ProfileService.show()

            // Show banner only if user is a referral_recipient and hasn't interacted with banner yet
            const shouldShowReferralRecipientBanner = profile && profile.show_referral_recipient_banner !== false
            const shouldShowReferralSenderBanner = profile && profile.show_referral_sender_banner !== false

            setState('showReferralRecipientBanner', shouldShowReferralRecipientBanner)
            setState('showReferralSenderBanner', shouldShowReferralSenderBanner)

            // TODO: use constants config or routing file param for referrals page
            // Check if user is on referrals page and the modal should be shown
            const isOnReferralsPage = location.pathname === '/referral-center'
            const shouldShowReferralSenderModal = profile && profile.show_referral_sender_modal

            if (isOnReferralsPage && shouldShowReferralSenderModal) {
                // Automatically show the referral_sender survey modal, hide the banner
                setState('showReferralSenderModal', true)
                setState('showReferralSenderBanner', false)
            }

        } catch (error) {
            console.error('Error checking banner visibility:', error)
            captureError(error)
            setState('showReferralRecipientBanner', false)
            setState('showReferralSenderBanner', false)
        }
    }

    onMount(async () => {
        await bookingStore.ensureCurrenciesLoaded()
        setIsAuthenticated(SessionService.isAuthenticated())
        if (isAuthenticated()) {
            await updateAuthenticatedData()
        }
        countStore.set({
            isLoaded: true,
        })
    })

    createEffect(async () => {
        try {
            setIsAuthenticated(SessionService.isAuthenticated())
            if (isAuthenticated()) {
                await updateAuthenticatedData()
            }
        } catch (error) {
            console.error('Error with auth state:', error)
            errorStore.addError(error, 'createEffect in public-template-component-controller')
        }
    })

    createEffect(async () => {
        if (isAuthenticated()) {
            await checkAndSetBannerVisibility()
        }
    })

    const updateAuthenticatedData = async () => {
        try {
            const [
                PropertyService,
                BookingService,
            ] = await Promise.all([
                loadPropertyService(),
                loadBookingService(),
            ])

            const properties = await PropertyService.list()
            if (properties) {
                countStore.set({
                    numOfProperties: properties.filter(p => !p.disconnected_at).length,
                })
            }

            const stays = await BookingService.listByProfileId()
            if (stays.bookingList) {
                countStore.set({
                    numOfStays: stays.bookingList.length,
                })
            }

            const bookings = await BookingService.listByPropertiesProfileId()
            if (bookings.bookingList) {
                countStore.set({
                    numOfBookings: bookings.bookingList.length,
                })
            }

            // Update inbox unread count (similar to bookings/stays pattern)
            // await pollUnreadConversations()

            countStore.set({
                isLoaded: true,
            })
        } catch (error) {
            console.error('Error fetching authenticated data:', error)
            errorStore.addError(error, 'updateAuthenticatedData')
        }
    }

    const getMenuItemProps = (path) => {
        return {
            class: 'public-template-component__main__sidebar__menu__item',
            classList: {
                'public-template-component__main__sidebar__menu__item--selected': location.pathname === path,
            },
            href: path,
        }
    }

    /**
     * The host calendar keeps the chosen listing in the path
     * (`/calendar/{property_id}`), so `getMenuItemProps`' exact-pathname
     * comparison stops matching the moment a listing is chosen. The sidebar
     * item has to stay lit for the whole surface, hence a prefix test.
     */
    const isCalendarRoute = () =>
        location.pathname === '/calendar' || location.pathname.startsWith('/calendar/')

    const navigateToDashboard = () => {
        trackEvent('clickCTAheader_OpenLodgr');
        posthogCapture('search_intent', {
            cta_location: 'header',
            currency: currentCurrency(),
        })
        // GA4/GTM mirror of the PostHog event (TENANT-4068).
        trackConversionEvent('search_intent', {
            parameters: {
                cta_location: 'header',
                currency: currentCurrency(),
            },
        })
        navigate('/search');
    }

    const navToWelcome = () => {
        if (!countStore.get('numOfProperties')) {
            navigate('/welcome?intent=become_host')
        } else if (!countStore.get('numOfStays')) {
            navigate('/welcome?intent=become_guest')
        }
        closeHamburgerMenu()
    }

    const getNavToWelcomeText = () => {
        if (!countStore.get('numOfProperties')) {
            return intl.formatMessage(messages.becomeAHostButton)
        } else if (!countStore.get('numOfStays')) {
            return intl.formatMessage(messages.becomeAGuestButton)
        }

        return null
    }

    const {
        openAuthModal: openGlobalAuthModal,
    } = useAuthModal()

    const openAuthModal = () => {
        // Use redirect URL from template context state if provided by page controllers
        const redirectUrl = state.authRedirectUrl || null
        openGlobalAuthModal(redirectUrl, false, null, null, 'header_login_button')
        trackEvent('clickCTAheader_Login')
        closeHamburgerMenu()
    }

    const closeAuthModal = () => {
        setShowAuthModal(false)
    }

    const handleLocaleSelect = () => {
        setState('showLocaleSelectModal', true)
        closeHamburgerMenu()
    }

    const closeLocaleSelectModal = () => {
        setState('showLocaleSelectModal', false)
    }

    // profile tooltip
    const [
        profileReference,
        setProfileReference,
    ] = createSignal(null)
    const [
        profileTooltipReference,
        setProfileTooltipReference,
    ] = createSignal(null)
    const [
        showTooltipLeft,
        setShowTooltipLeft,
    ] = createSignal(false)
    const [
        profileTooltipVisible,
        setProfileTooltipVisible,
    ] = createSignal(false)

    let profileTooltipTimeoutId

    const showProfileTooltip = () => {
        checkTooltipPosition()
        profileTooltipTimeoutId = setTimeout(() => setProfileTooltipVisible(true), 300); // Delay 300ms
    };

    const hideProfileTooltip = () => {
        clearTimeout(profileTooltipTimeoutId);
        setProfileTooltipVisible(false);
    };

    const checkTooltipPosition = () => {
        const profileElement = profileReference()
        const profileTooltipElement = profileTooltipReference()

        if (!profileElement || !profileTooltipElement) return

        const rect = profileElement.getBoundingClientRect()
        const profileWidth = profileElement.offsetWidth
        const profileTooltipWidth = profileTooltipElement.offsetWidth
        const screenWidth = window.innerWidth

        setShowTooltipLeft(profileTooltipWidth - profileWidth > screenWidth - rect.right)
    }

    const handleToggleWelcome = () => {
        if (!countStore.get('numOfProperties')
            && countStore.get('numOfStays')) {
            trackEvent('clickAccordian_BecomeAhost')
        } else if (countStore.get('numOfProperties')
            && !countStore.get('numOfStays')) {
            trackEvent('clickAccordian_BecomeAguest')
        }
    }

    // TODO: Add handler for support link
    const handleSupportClick = () => {
        trackEvent('ClickCTAheader_Support');
    }

    const handleNewsroomClick = () => {
        trackEvent('clickCTAfromFooter_LodgrNewsroom')
        navigate('/newsroom')
        closeHamburgerMenu()
    }

    const handleExtensionClick = () => {
        navigate('/browser-extension')
        closeHamburgerMenu()
    }

    const handleHostClick = () => {
        navigate('/become-a-host')
        closeHamburgerMenu()
    }

    const closeHamburgerMenu = () => {
        if (showHamburgerMenu()) {
            setShowHamburgerMenu(false)
        }
    }

    const handleLogoClick = () => {
        closeHamburgerMenu()
    }

    // Handlers for sidebar/accordion menu items
    const handleReferralsClick = () => {
        trackEvent('clickAccordian_referrals');
        closeHamburgerMenu()
    }

    const handlePropertiesClick = () => {
        trackEvent('clickAccordian_Properties');
        closeHamburgerMenu()
    }

    const handleBookingsClick = () => {
        trackEvent('clickAccordian_Bookings');
        closeHamburgerMenu()
    }

    const handleCalendarClick = () => {
        trackEvent('clickAccordian_Calendar');
        closeHamburgerMenu()
    }

    const handleSearchClick = () => {
        posthogCapture('search_intent', {
            cta_location: 'header',
            currency: currentCurrency(),
        })
        navigate('/search')
        closeHamburgerMenu()
    }

    const handleStaysClick = () => {
        trackEvent('clickAccordianCTA_stays');
        closeHamburgerMenu()
    }

    const handleInboxClick = () => {
        // TODO: Add event tracking?
        // trackEvent('clickAccordian_Inbox');
        closeHamburgerMenu()
    }

    const handleAccountClick = () => {
        trackEvent('clickAccordian_Account');
        closeHamburgerMenu()
    }

    // Legal links in the mobile app's hamburger (the footer that normally carries
    // them is not rendered in the app). No analytics event: these are not a
    // marketing surface, unlike the FAQ entry below.
    const handleLegalLinkClick = () => {
        closeHamburgerMenu()
    }

    // TENANT-5565: Lodgr Insights in the app's hamburger. No analytics (James, 2026-10-05).
    const handleInsightsClick = () => {
        closeHamburgerMenu()
    }

    const handleFaqClick = () => {
        trackEvent('clickAccordianCTA_FAQ');
        trackEvent('Triggered_FAQ');
        closeHamburgerMenu()
    }

    const openReferralRecipientModal = () => {
        setState('showReferralRecipientModal', true)
    }

    const openReferralSenderModal = async () => {
        setState('showReferralSenderBanner', false)
        setState('showReferralSenderModal', true)

        const currentDateTime = new Date()

        try {
            const ProfileService = await loadProfileService()
            await ProfileService.update({
                referral_sender_banner_interacted_at: currentDateTime,
            })
        } catch (error) {
            console.error('Error updating referral_sender banner:', error)
            captureError(error) // so it can be captured by Sentry

        }
    }

    const closeReferralSenderModal = () => {
        setState('showReferralSenderModal', false)
    }

    const closeReferralRecipientModal = () => {
        setState('showReferralRecipientModal', false)
    }

    const hideReferralSenderBanner = async () => {
        setState('showReferralSenderBanner', false)

        const currentDateTime = new Date()

        try {
            const ProfileService = await loadProfileService()
            await ProfileService.update({
                referral_sender_banner_interacted_at: currentDateTime,
            })
        } catch (error) {
            captureError(error) // so it can be captured by Sentry
            console.error('Error updating referral_sender banner:', error)
        }

    }

    const [
        referralRecipientFeedbackRating,
        setReferralRecipientFeedbackRating,
    ] = createSignal({})

    const handleReferralRecipientFeedback = async (value) => {
        try {
            setReferralRecipientFeedbackRating({
                signup_satisfaction: value,
            })

            // Mark banner as interacted with current timestamp
            const currentDateTime = new Date()
            const ProfileService = await loadProfileService()
            await ProfileService.update({
                referral_recipient_banner_interacted_at: currentDateTime,
            })

            // Hide the banner
            setState('showReferralRecipientBanner', false)

            // Open the modal for additional feedback
            setState('showReferralRecipientModal', true)
        } catch (error) {
            console.error('Error handling referral_recipient feedback:', error)
            captureError(error) // so it can be captured by Sentry

            // Still hide banner even if API call fails
            setState('showReferralRecipientBanner', false)
        }
    }

    const hideReferralRecipientBanner = (value) => {
        setState('showReferralRecipientBanner', false)
        // Pass value to referral_recipient modal later
        openReferralRecipientModal()
    }

    const getReferralRecipientFeedbackOptions = () => {
        return [
            {
                label: intl.formatMessage(messages.referralRecipientFeedbackConfusing),
                path: 'icons/face-bad-outlined',
                value: 1,
            },
            {
                label: intl.formatMessage(messages.referralRecipientFeedbackOkay),
                path: 'icons/face-neutral-outlined',
                value: 2,
            },
            {
                label: intl.formatMessage(messages.referralRecipientFeedbackSmooth),
                path: 'icons/face-good-outlined',
                value: 3,
            },
        ]
    }

    const CONV_POLLING_INTERVAL_MS = 7500 // Match inbox page polling rate for quick updates

    const pollUnreadConversations = async () => {
        // returns false if api sync is already taking place
        if (inboxStore.get('isUnreadConvosCountSyncing')) return
        try {
            inboxStore.startUnreadConvosCountSync()
            const InboxService = await loadInboxService()
            const conversations = await InboxService.getConversations( {
                size: 25,
            } )
            if (!conversations) return
            const unreadConvosCount = conversations.filter(c => c.unread).length

            if (unreadConvosCount !== inboxStore.get('unreadConvosCount')) {
                inboxStore.setUnreadConvosCount(unreadConvosCount)
            }

        } catch (error) {
            console.error('Failed to fetch unread Count', error)
        } finally {
            inboxStore.finishUnreadConvosCountSync()
        }
    }

    const unreadConvosCount = () => inboxStore.get('unreadConvosCount')

    // Poll inbox unread count (similar pattern to bookings/stays, but with continuous polling)
    createEffect(() => {
        if (!isAuthenticated()) return

        const isOnInboxPage = location.pathname === '/inbox'

        // Skip nav bar polling when on inbox page - page-level polling will handle it
        if (isOnInboxPage) return

        // Immediately update when leaving inbox page, then start polling
        // Note: Temp disabled polling as it was looping when auth fails (specifically when an invalidsession_access_token is provided?)
        // pollUnreadConversations()
        // const interval = setInterval(pollUnreadConversations, CONV_POLLING_INTERVAL_MS)
        // onCleanup(() => clearInterval(interval))
    })


    return {
        attrs: attributes,
        bookingStore,
        closeAuthModal,
        closeLocaleSelectModal,
        closeReferralRecipientModal,
        closeReferralSenderModal,
        countStore,
        getMenuItemProps,
        getNavToWelcomeText,
        getReferralRecipientFeedbackOptions,
        handleAccountClick,
        handleBookingsClick,
        handleCalendarClick,
        handleExtensionClick,
        handleFaqClick,
        handleHostClick,
        handleInboxClick,
        handleInsightsClick,
        handleLegalLinkClick,
        handleLocaleSelect,
        handleLogoClick,
        handleNewsroomClick,
        handlePropertiesClick,
        handleReferralRecipientFeedback,
        handleReferralsClick,
        handleSearchClick,
        handleStaysClick,
        handleSupportClick,
        handleToggleWelcome,
        hideProfileTooltip,
        hideReferralRecipientBanner,
        hideReferralSenderBanner,
        isAuthenticated,
        isCalendarRoute,
        logoHref,
        logout,
        navToWelcome,
        navigateToDashboard,
        openAuthModal,
        openReferralRecipientModal,
        openReferralSenderModal,
        profileReference,
        profileTooltipReference,
        profileTooltipVisible,
        properties,
        referralRecipientFeedbackRating,
        reqAttrs: requestAttributes,
        sessionStore,
        setProfileReference,
        setProfileTooltipReference,
        showAuthModal,
        showHamburgerMenu,
        showProfileTooltip,
        showTooltipLeft,
        state,
        stays,
        styles,
        toggleHamburgerMenu,
        unreadConvosCount,
        value,
    }
}
